import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';

import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import ReportTemplateCard from './components/ReportTemplateCard';
import GeneratedReportsList from './components/GeneratedReportsList';
import ReportPreviewModal from './components/ReportPreviewModal';
import { reportService } from '../../lib/posService';
import { gymService } from '../../lib/services';

const AutomatedReportGeneration = () => {
  const { userProfile } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [generatedReports, setGeneratedReports] = useState([]);
  const [selectedGym, setSelectedGym] = useState(null);
  const [gyms, setGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [previewReport, setPreviewReport] = useState(null);
  const [activeTab, setActiveTab] = useState('templates');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    
    // Load gyms
    const { data: gymsData } = await gymService?.getActiveGyms();
    if (gymsData && gymsData?.length > 0) {
      setGyms(gymsData);
      setSelectedGym(gymsData?.[0]?.id);
    }

    // Load templates
    const { data: templatesData } = await reportService?.getReportTemplates();
    if (templatesData) {
      setTemplates(templatesData);
    }

    setLoading(false);
  };

  const loadGeneratedReports = async () => {
    if (!selectedGym) return;
    const { data } = await reportService?.getGeneratedReports(selectedGym);
    if (data) {
      setGeneratedReports(data);
    }
  };

  useEffect(() => {
    if (selectedGym && activeTab === 'history') {
      loadGeneratedReports();
    }
  }, [selectedGym, activeTab]);

  const handleGenerateReport = async (template, dateRange) => {
    if (!selectedGym) {
      alert('Please select a gym location');
      return;
    }

    setGenerating(template?.id);
    const { data, error } = await reportService?.generateReport(selectedGym, template?.id, dateRange);
    
    if (!error && data) {
      setPreviewReport(data);
      if (activeTab === 'history') {
        loadGeneratedReports();
      }
    } else {
      alert(error?.message || 'Failed to generate report');
    }
    
    setGenerating(null);
  };

  const getReportTypeIcon = (type) => {
    const icons = {
      financial: 'DollarSign',
      membership: 'Users',
      operations: 'Activity',
      inventory: 'Package',
      custom: 'FileText'
    };
    return icons?.[type] || 'FileText';
  };

  const getReportTypeColor = (type) => {
    const colors = {
      financial: 'var(--color-success)',
      membership: 'var(--color-primary)',
      operations: 'var(--color-accent)',
      inventory: 'var(--color-warning)',
      custom: 'var(--color-muted-foreground)'
    };
    return colors?.[type] || 'var(--color-muted-foreground)';
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <DashboardNavigation />
      </div>

      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                Automated Report Generation
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Generate comprehensive business reports with customizable templates
              </p>
            </div>
          </div>

          {/* Gym Selector */}
          {gyms?.length > 1 && (
            <div className="bg-card rounded-xl p-4 shadow-md border border-border mb-6">
              <div className="flex items-center gap-4">
                <Icon name="Building" size={20} color="var(--color-muted-foreground)" />
                <div className="flex gap-2 flex-wrap">
                  {gyms?.map(gym => (
                    <button
                      key={gym?.id}
                      onClick={() => setSelectedGym(gym?.id)}
                      className={`
                        px-4 py-2 rounded-lg text-sm font-medium transition-colors
                        ${selectedGym === gym?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-foreground hover:bg-muted/80'
                        }
                      `}
                    >
                      {gym?.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="bg-card rounded-xl shadow-md border border-border mb-6">
            <div className="flex border-b border-border">
              <button
                onClick={() => setActiveTab('templates')}
                className={`
                  flex-1 px-6 py-4 text-sm font-medium transition-colors
                  ${activeTab === 'templates' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                  }
                `}
              >
                <div className="flex items-center justify-center gap-2">
                  <Icon name="FileText" size={18} />
                  <span>Report Templates</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`
                  flex-1 px-6 py-4 text-sm font-medium transition-colors
                  ${activeTab === 'history' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                  }
                `}
              >
                <div className="flex items-center justify-center gap-2">
                  <Icon name="History" size={18} />
                  <span>Generated Reports</span>
                </div>
              </button>
            </div>
          </div>

          {/* Content */}
          {activeTab === 'templates' ? (
            <div>
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3, 4, 5, 6]?.map(i => (
                    <div key={i} className="bg-card rounded-xl p-6 shadow-md border border-border animate-pulse">
                      <div className="h-32 bg-muted rounded-lg"></div>
                    </div>
                  ))}
                </div>
              ) : templates?.length === 0 ? (
                <div className="bg-card rounded-xl p-12 shadow-md border border-border text-center">
                  <Icon name="FileText" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                  <p className="text-muted-foreground">No report templates available</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {templates?.map(template => (
                    <ReportTemplateCard
                      key={template?.id}
                      template={template}
                      onGenerate={handleGenerateReport}
                      generating={generating === template?.id}
                      icon={getReportTypeIcon(template?.report_type)}
                      iconColor={getReportTypeColor(template?.report_type)}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <GeneratedReportsList
              reports={generatedReports}
              onPreview={setPreviewReport}
              loading={loading}
            />
          )}
        </div>
      </main>

      {previewReport && (
        <ReportPreviewModal
          report={previewReport}
          onClose={() => setPreviewReport(null)}
        />
      )}
    </div>
  );
};

export default AutomatedReportGeneration;